import { ArrowLeft, Ellipsis } from "lucide-react";
import { useCallback, useDeferredValue, useEffect, useRef, useState } from "react";
import type { Marker, TermBlock, TermDetail, TermSummary } from "@termclear/contracts";
import { api, ApiError } from "./api";
import { representsSameDetail, type DetailIdentity } from "./detail-scroll";
import { ChatSheet } from "./components/ChatSheet";
import { BrandMark } from "./components/BrandMark";
import { LibraryView, type LibraryFilters } from "./components/LibraryView";
import { LoginView } from "./components/LoginView";
import { MarkerSheet } from "./components/MarkerSheet";
import { SiteFilingFooter } from "./components/SiteFilingFooter";
import { TermDetailView } from "./components/TermDetailView";
import { VersionsSheet } from "./components/VersionsSheet";
import { playUiSound } from "./ui-sound";
import {
  currentEntryCanGoBack,
  currentNavigation,
  detailNavigation,
  libraryNavigation,
  pushNavigation,
  replaceNavigation,
  type AppNavigation,
} from "./navigation";

const initialFilters: LibraryFilters = {
  search: "",
  collection: "all",
  category: "all",
  state: "all",
  sort: "random",
};

function makeRandomSeed(): number {
  const values = new Uint32Array(1);
  crypto.getRandomValues(values);
  return (values[0] ?? 1) & 0x7fffffff;
}

export function App() {
  const [authState, setAuthState] = useState<"checking" | "anonymous" | "authenticated">("checking");
  const [items, setItems] = useState<TermSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [categories, setCategories] = useState<Array<{ value: string; count: number }>>([]);
  const [collections, setCollections] = useState<Array<{ value: "technical" | "english"; count: number }>>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [filters, setFilters] = useState(initialFilters);
  const deferredSearch = useDeferredValue(filters.search);
  const [randomSeed, setRandomSeed] = useState(makeRandomSeed);
  const [loadingList, setLoadingList] = useState(false);
  const [detail, setDetail] = useState<TermDetail | null>(null);
  const [reviewMode, setReviewMode] = useState(false);
  const [activeChatBlock, setActiveChatBlock] = useState<TermBlock | null>(null);
  const [activeChatDraftId, setActiveChatDraftId] = useState<string | null>(null);
  const [activeMarkerBlock, setActiveMarkerBlock] = useState<TermBlock | null>(null);
  const [versionsOpen, setVersionsOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [toast, setToast] = useState("");
  const [modelLabel, setModelLabel] = useState("DeepSeek V4 Flash");
  const listRequest = useRef(0);
  const listLoading = useRef(false);
  const loadMorePromise = useRef<Promise<TermSummary[]> | null>(null);
  const navigationRequest = useRef(0);
  const renderedDetail = useRef<DetailIdentity | null>(null);
  const overlayScrollPosition = useRef<number | null>(null);
  const ratingOperation = useRef<{ termId: string; rating: number; key: string } | null>(null);

  useEffect(() => {
    replaceNavigation(currentNavigation());
    api.restoreSession()
      .then((session) => setAuthState(session ? "authenticated" : "anonymous"))
      .catch(() => setAuthState("anonymous"));
  }, []);

  const handleAuthError = useCallback((error: unknown) => {
    if (error instanceof ApiError && error.status === 401) {
      setAuthState("anonymous");
      setDetail(null);
      setActiveChatBlock(null);
      setActiveChatDraftId(null);
      setActiveMarkerBlock(null);
      setVersionsOpen(false);
      renderedDetail.current = null;
      overlayScrollPosition.current = null;
      replaceNavigation(libraryNavigation());
      return;
    }
    setToast(error instanceof Error ? error.message : "没有完成请求");
  }, []);

  const applyNavigation = useCallback(async (navigation: AppNavigation): Promise<boolean> => {
    const requestId = ++navigationRequest.current;
    const sameDetail = representsSameDetail(renderedDetail.current, navigation);
    const scrollToRestore = !navigation.sheet && sameDetail
      ? overlayScrollPosition.current
      : null;
    if (navigation.sheet && overlayScrollPosition.current === null) {
      overlayScrollPosition.current = window.scrollY;
    }
    setMenuOpen(false);
    setActiveChatBlock(null);
    setActiveChatDraftId(null);
    setActiveMarkerBlock(null);
    setVersionsOpen(false);
    if (!navigation.termId) {
      setDetail(null);
      setReviewMode(false);
      renderedDetail.current = null;
      overlayScrollPosition.current = null;
      return true;
    }

    try {
      const value = await api.getTerm(navigation.termId);
      if (requestId !== navigationRequest.current) return false;
      setDetail(value);
      setReviewMode(navigation.reviewMode);
      renderedDetail.current = { termId: value.id, reviewMode: navigation.reviewMode };
      const block = navigation.blockId
        ? value.blocks.find((candidate) => candidate.id === navigation.blockId) ?? null
        : null;
      if (navigation.sheet === "chat" && block) {
        const draftId = navigation.draftId ?? crypto.randomUUID();
        setActiveChatBlock(block);
        setActiveChatDraftId(draftId);
        if (!navigation.draftId) replaceNavigation({ ...navigation, draftId });
      } else if (navigation.sheet === "marker" && block) {
        setActiveMarkerBlock(block);
      } else if (navigation.sheet === "versions") {
        setVersionsOpen(true);
      } else if (navigation.sheet) {
        replaceNavigation(detailNavigation(value.id, navigation.reviewMode));
      }
      if (!sameDetail) {
        window.scrollTo({ top: 0, behavior: "instant" });
      } else if (scrollToRestore !== null) {
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(() => {
            window.scrollTo({ top: scrollToRestore, behavior: "instant" });
          });
        });
      }
      if (!navigation.sheet) overlayScrollPosition.current = null;
      return true;
    } catch (error) {
      if (requestId === navigationRequest.current) handleAuthError(error);
      return false;
    }
  }, [handleAuthError]);

  useEffect(() => {
    if (authState !== "authenticated") return;
    void applyNavigation(currentNavigation());
    const onPopState = () => { void applyNavigation(currentNavigation()); };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [applyNavigation, authState]);

  useEffect(() => {
    if (authState !== "authenticated") return;
    api.getModelRuntime()
      .then((runtime) => setModelLabel(runtime.label))
      .catch(handleAuthError);
  }, [authState]);

  useEffect(() => {
    if (authState !== "authenticated") return;
    const requestId = ++listRequest.current;
    listLoading.current = true;
    setLoadingList(true);
    api.listTerms({
      ...filters,
      search: deferredSearch,
      randomSeed,
    }).then((result) => {
      if (requestId !== listRequest.current) return;
      setItems(result.items);
      setTotal(result.total);
      setCategories(result.categories);
      setCollections(result.collections);
      setCursor(result.nextCursor);
    }).catch(handleAuthError).finally(() => {
      if (requestId === listRequest.current) {
        listLoading.current = false;
        setLoadingList(false);
      }
    });
  }, [authState, deferredSearch, filters.category, filters.collection, filters.sort, filters.state, randomSeed]);

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(""), 2800);
    return () => window.clearTimeout(timer);
  }, [toast]);

  useEffect(() => {
    if (!detail || loadingList || items.some((item) => item.id === detail.id) || !cursor) return;
    void loadMore();
  }, [cursor, detail?.id, items, loadingList]);

  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (!detail || activeChatBlock || activeMarkerBlock || versionsOpen) return;
      if (event.key === "ArrowRight") void move(1);
      if (event.key === "ArrowLeft") void move(-1);
      if (event.key === "Escape") showLibrary();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  async function login(password: string) {
    await api.login(password);
    setAuthState("authenticated");
  }

  async function logout() {
    try {
      await api.logout();
    } finally {
      setAuthState("anonymous");
      setDetail(null);
      setMenuOpen(false);
      setActiveChatBlock(null);
      setActiveChatDraftId(null);
      setActiveMarkerBlock(null);
      setVersionsOpen(false);
      renderedDetail.current = null;
      overlayScrollPosition.current = null;
      replaceNavigation(libraryNavigation());
    }
  }

  function changeFilters(next: LibraryFilters) {
    if (filters.sort !== next.sort && next.sort === "random") setRandomSeed(makeRandomSeed());
    setFilters(next);
  }

  function loadMore(): Promise<TermSummary[]> {
    if (loadMorePromise.current) return loadMorePromise.current;
    if (!cursor || listLoading.current) return Promise.resolve([]);
    const requestId = ++listRequest.current;
    listLoading.current = true;
    setLoadingList(true);
    const work = api.listTerms({
      search: deferredSearch,
      collection: filters.collection,
      category: filters.category,
      state: filters.state,
      sort: filters.sort,
      cursor,
      randomSeed,
    }).then((result) => {
      if (requestId !== listRequest.current) return [];
      setItems((current) => {
        const existing = new Set(current.map((item) => item.id));
        return [...current, ...result.items.filter((item) => !existing.has(item.id))];
      });
      setCursor(result.nextCursor);
      setTotal(result.total);
      setCategories(result.categories);
      setCollections(result.collections);
      return result.items;
    }).catch((error: unknown) => {
      handleAuthError(error);
      return [];
    }).finally(() => {
      if (requestId === listRequest.current) {
        listLoading.current = false;
        setLoadingList(false);
      }
      if (loadMorePromise.current === work) loadMorePromise.current = null;
    });
    loadMorePromise.current = work;
    return work;
  }

  async function openTerm(
    termId: string,
    asReview: boolean,
    mode: "push" | "replace" = "push",
  ): Promise<boolean> {
    const navigation = detailNavigation(termId, asReview);
    if (!await applyNavigation(navigation)) return false;
    if (mode === "push") pushNavigation(navigation);
    else replaceNavigation(navigation);
    return true;
  }

  function showLibrary() {
    if (currentEntryCanGoBack()) {
      window.history.back();
      return;
    }
    const navigation = libraryNavigation();
    replaceNavigation(navigation);
    void applyNavigation(navigation);
  }

  function openChat(block: TermBlock) {
    if (!detail) return;
    overlayScrollPosition.current = window.scrollY;
    const draftId = crypto.randomUUID();
    const navigation: AppNavigation = {
      ...detailNavigation(detail.id, reviewMode),
      sheet: "chat",
      blockId: block.id,
      draftId,
    };
    setActiveChatBlock(block);
    setActiveChatDraftId(draftId);
    pushNavigation(navigation);
  }

  function openMarker(block: TermBlock) {
    if (!detail) return;
    overlayScrollPosition.current = window.scrollY;
    setActiveMarkerBlock(block);
    pushNavigation({
      ...detailNavigation(detail.id, reviewMode),
      sheet: "marker",
      blockId: block.id,
    });
  }

  function openVersions() {
    if (!detail) return;
    overlayScrollPosition.current = window.scrollY;
    setVersionsOpen(true);
    setMenuOpen(false);
    pushNavigation({ ...detailNavigation(detail.id, reviewMode), sheet: "versions" });
  }

  function closeOverlay(discardCompletedEntry = false) {
    if (!detail) return;
    const navigation = detailNavigation(detail.id, reviewMode);
    if (discardCompletedEntry) replaceNavigation(navigation);
    if (currentEntryCanGoBack()) {
      window.history.back();
      return;
    }
    replaceNavigation(navigation);
    void applyNavigation(navigation);
  }

  async function move(offset: number) {
    if (!detail) return;
    const index = items.findIndex((item) => item.id === detail.id);
    if (index < 0) {
      setToast("当前词条不在这轮筛选结果中，请返回词库重新打开");
      return;
    }
    let next = items[index + offset];
    if (!next && offset > 0 && cursor) {
      const additionalItems = await loadMore();
      next = additionalItems[0];
    }
    if (!next) {
      setToast(offset > 0 ? "已经是全部结果的最后一条" : "已经是全部结果的第一条");
      return;
    }
    if (await openTerm(next.id, reviewMode, "replace")) void playUiSound("page");
  }

  async function refreshDetail() {
    if (!detail) return;
    const value = await api.getTerm(detail.id);
    setDetail(value);
    setItems((current) => current.map((item) => item.id === value.id ? {
      ...item,
      needUnderstanding: value.needUnderstanding,
      markerCount: value.markers.filter((marker) => marker.status === "open").length,
      updatedAt: value.updatedAt,
    } : item));
  }

  function handleProposalApplied() {
    closeOverlay(true);
    setToast("已应用为新版本");
    void refreshDetail().catch((error) => {
      if (error instanceof ApiError && error.status === 401) {
        handleAuthError(error);
        return;
      }
      setToast("新版本已应用，但页面刷新失败，请重新打开术语");
    });
  }

  function handleMarkerSaved(marker: Marker) {
    if (!detail) return;
    closeOverlay(true);
    const termId = detail.id;
    setDetail((current) => current?.id === termId
      ? { ...current, markers: [...current.markers, marker] }
      : current);
    setItems((current) => current.map((item) => item.id === termId
      ? { ...item, markerCount: item.markerCount + 1 }
      : item));
    setToast("标记已保存");
  }

  async function updateRating(rating: number) {
    if (!detail) return;
    const operation = ratingOperation.current?.termId === detail.id
      && ratingOperation.current.rating === rating
      ? ratingOperation.current
      : { termId: detail.id, rating, key: crypto.randomUUID() };
    ratingOperation.current = operation;
    const previous = detail.needUnderstanding;
    setDetail({ ...detail, needUnderstanding: rating });
    try {
      await api.setRating(detail.id, rating, operation.key);
      ratingOperation.current = null;
      setItems((current) => current.map((item) => item.id === detail.id ? { ...item, needUnderstanding: rating } : item));
      setToast(`已设为“需理解 ${rating} 星”`);
    } catch (error) {
      setDetail((current) => current ? { ...current, needUnderstanding: previous } : current);
      handleAuthError(error);
      throw error;
    }
  }

  if (authState === "checking") {
    return (
      <main className="boot-view">
        <div className="boot-brand"><BrandMark /><p className="wordmark">TermClear</p></div>
        <span>正在确认私密访问…</span>
      </main>
    );
  }
  if (authState === "anonymous") return <LoginView onLogin={login} />;

  const currentIndex = detail ? items.findIndex((item) => item.id === detail.id) : -1;

  return (
    <>
      <a className="skip-link" href="#main">跳到主要内容</a>
      <div className="app-shell">
        {detail ? (
          <header className="app-header">
            <button className="icon-button" type="button" aria-label="返回词库" onClick={showLibrary}>
              <ArrowLeft aria-hidden="true" />
            </button>
            <button className="header-library-link" type="button" onClick={showLibrary}>词库</button>
            <div className="header-actions">
              <button className="icon-button" type="button" aria-label="打开菜单" aria-expanded={menuOpen} onClick={() => setMenuOpen((open) => !open)}>
                <Ellipsis aria-hidden="true" />
              </button>
            </div>
            {menuOpen ? (
              <div className="small-menu">
                <button type="button" onClick={openVersions}>版本与标记</button>
                <button type="button" onClick={() => void logout()}>退出登录</button>
              </div>
            ) : null}
          </header>
        ) : null}
        <main id="main">
          {detail ? (
            <TermDetailView
              key={`${detail.id}:${reviewMode}`}
              term={detail}
              reviewMode={reviewMode}
              position={currentIndex >= 0 ? currentIndex + 1 : null}
              total={total}
              onBack={showLibrary}
              onPrevious={() => void move(-1)}
              onNext={() => void move(1)}
              onRating={updateRating}
              onChat={openChat}
              onMarker={openMarker}
            />
          ) : (
            <LibraryView
              items={items}
              total={total}
              categories={categories}
              collections={collections}
              loading={loadingList}
              hasMore={Boolean(cursor)}
              filters={filters}
              onFilters={changeFilters}
              onLoadMore={() => void loadMore()}
              onOpen={(id, review) => void openTerm(id, review)}
              onStartReview={() => items[0] ? void openTerm(items[0].id, true) : setToast("当前没有可以浏览的术语")}
              menuOpen={menuOpen}
              onToggleMenu={() => setMenuOpen((open) => !open)}
              onLogout={() => void logout()}
            />
          )}
        </main>
        <SiteFilingFooter />
      </div>
      {detail && activeChatBlock && activeChatDraftId ? (
        <ChatSheet
          key={activeChatDraftId}
          term={detail}
          block={activeChatBlock}
          draftId={activeChatDraftId}
          modelLabel={modelLabel}
          onClose={() => closeOverlay()}
          onApplied={handleProposalApplied}
        />
      ) : null}
      {detail && activeMarkerBlock ? (
        <MarkerSheet
          term={detail}
          block={activeMarkerBlock}
          onClose={() => closeOverlay()}
          onSaved={handleMarkerSaved}
        />
      ) : null}
      {detail && versionsOpen ? <VersionsSheet term={detail} onClose={() => closeOverlay()} /> : null}
      {toast ? <div className="toast" role="status" aria-live="polite">{toast}</div> : null}
    </>
  );
}
